import { Component, Element, Event, EventEmitter, Build, Method, Host, h } from '@stencil/core';

export interface mamaFormControl extends HTMLElement {
  checkValidity(): Promise<boolean>;

  setFocus(): Promise<void>;
}

const asyncFilter = (array: Array<mamaFormControl>, filterFn: Function) => {
  return new Promise(resolve => {
    const booleanArr = [];
    array.forEach(function (e) {
      booleanArr.push(filterFn(e));
    });
    Promise.all(booleanArr).then(result => resolve(array.filter((_, i) => result[i])));
  });
};

const elementInViewport = (el: HTMLElement) => {
  let top = el.offsetTop;
  let left = el.offsetLeft;
  let width = el.offsetWidth;
  let height = el.offsetHeight;
  let parent = el;

  while (parent.offsetParent) {
    parent = parent.offsetParent as HTMLElement;
    top += parent.offsetTop;
    left += parent.offsetLeft;
  }

  return top < window.pageYOffset + window.innerHeight && left < window.pageXOffset + window.innerWidth && top + height > window.pageYOffset && left + width > window.pageXOffset;
};

@Component({
  tag: 'mama-form',
  styleUrl: './mama-form.scss',
})
export class MamaForm {
  @Element() el!: HTMLElement;
  @Event({ eventName: 'submit' }) formSubmit!: EventEmitter<void>;
  private onSubmitListener: (event) => void;
  private inputs: NodeListOf<mamaFormControl>;

  componentDidLoad() {
    if (!Build.isBrowser) {
      return;
    }
    this.onSubmitListener = this.onSubmit.bind(this);
    this.form.setAttribute('novalidate', 'novalidate');
    this.form.addEventListener('submit', this.onSubmitListener);
    this.inputs = this.el.querySelectorAll('input,textarea,select,mama-select') as NodeListOf<mamaFormControl>;
    this.inputs.forEach((input: mamaFormControl) => {
      input.addEventListener('blur', () => {
        this.setFieldState(input);
      });
    });
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.onSubmitListener);
  }

  @Method()
  async checkValidity(): Promise<boolean> {
    const invalidInputs = await this.invalidInputs();
    return invalidInputs.length == 0 && this.form.checkValidity();
  }

  async invalidInputs(): Promise<Array<mamaFormControl>> {
    return (await asyncFilter(Array.from(this.inputs), async el => !(await el.checkValidity()))) as Array<mamaFormControl>;
  }

  setFieldState(input: mamaFormControl) {
    const parent = this.getParentOfInput(input);
    if (input.checkValidity()) {
      parent.classList.remove('is-invalid');
    } else {
      parent.classList.add('is-invalid');
    }
  }

  getParentOfInput(field: mamaFormControl) {
    let parent = field.parentElement;

    // Needed for friendly captcha
    if (parent === null) {
      return field;
    }

    if (parent && parent.classList.contains('form-group')) {
      return parent;
    }

    if (parent && parent.parentElement && parent.parentElement.classList.contains('form-group')) {
      return parent.parentElement;
    }

    return field;
  }

  protected async onSubmit(event) {
    const isValid = await this.checkValidity();
    if (isValid) {
      this.formSubmit.emit();
    } else {
      event.preventDefault();
      event.stopPropagation();
      const invalidInputs = await this.invalidInputs();
      if (invalidInputs.length) {
        this.inputs.forEach((input: mamaFormControl) => {
          this.setFieldState(input);
        });
        const invalidInput = invalidInputs[0] as any as HTMLInputElement;
        invalidInput.reportValidity();
        if (!elementInViewport(invalidInput)) {
          window.requestAnimationFrame(() => invalidInput.scrollIntoView());
        }
      }
    }
  }

  get form(): HTMLFormElement {
    return this.el.querySelector('form');
  }

  render() {
    return (
      <Host>
        <slot />
      </Host>
    );
  }
}
